import { useQuery } from '@apollo/client/react';
import { Navigate, useLocation } from 'react-router';
import { graphql } from '@/gql';
import { useSession } from '@/features/session/useSession';

const StaffCompletenessDoc = graphql(/* GraphQL */ `
  query StaffCompleteness {
    me {
      completeness {
        status
        missingFields {
          name
          label
          kind
          required
          options
        }
      }
    }
  }
`);

const ALLOWED_WHILE_INCOMPLETE = new Set([
  '/onboarding',
  '/profile',
  '/login',
  '/register-passkey',
]);

/**
 * Navigation guard for CheckCompleteness `action_required` (1.3).
 *
 * Reads `me.completeness` (staff-safe; same CheckCompleteness payload as
 * ExpoApp). While the Go API is an older binary without that field, the query
 * fails closed to "not required" so the rest of admin stays usable.
 */
export function ActionRequiredGuard({ children }: { children: React.ReactNode }) {
  const { me, loading: meLoading } = useSession();
  const location = useLocation();
  const { data, loading } = useQuery(StaffCompletenessDoc, {
    skip: meLoading || !me,
    errorPolicy: 'ignore',
    fetchPolicy: 'cache-and-network',
  });

  if (meLoading || loading) {
    return children;
  }

  const status = data?.me?.completeness?.status;
  if (status !== 'ACTION_REQUIRED') {
    return children;
  }

  const path = location.pathname;
  const allowed = [...ALLOWED_WHILE_INCOMPLETE].some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
  if (allowed) {
    return children;
  }

  return <Navigate to="/onboarding" replace />;
}
